import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Avatar,
  Box,
  CircularProgress,
  Popper,
  Typography,
} from '@mui/material';
// import { MASTER_ENDPOINT } from 'utils/Constant';
import React from 'react';
import { toast } from 'react-toastify';
import { ReplaceData } from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { alertOptions } from 'utils/Config';
import { MASTER_ENDPOINT } from 'utils/Constant';

import UserAssignModal from './UserAssignModal';
import { JobData, JobUserAssigned, User } from './utils';

type AssignUserListProps = {
  users: User[];
  row: JobData;
};

const AssignUserList: React.FC<AssignUserListProps> = React.memo(
  ({ users, row }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [assignModalOpen, setAssignModalOpen] = React.useState(false);
    const [selectedJobId, setSelectedJobId] = React.useState<string>('');
    const [selectedJobTitle, setSelectedJobTitle] = React.useState<string>('');
    const [assignedUsers, setAssignedUsers] = React.useState<JobUserAssigned[]>(
      [],
    );
    const [loader, setLoader] = React.useState(false);

    const { userInfo: userDetail }: any = useAppSelector(
      (store: any) => store.userLogin,
    );

    // Memoize assignedUsers to avoid unnecessary re-renders unless row.usersAssigned changes
    React.useEffect(() => {
      if (assignedUsers.length > 0) return;
      setAssignedUsers(row.usersAssigned || []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAssignUser = React.useCallback(
      (jobData: JobData, anchorEl: HTMLElement | null) => {
        setAnchorEl(anchorEl);
        setSelectedJobId(jobData.id);
        setAssignModalOpen(true);
      },
      [],
    );

    const handleCloseAssignModal = React.useCallback(() => {
      setAssignModalOpen(false);
      setSelectedJobId('');
      setSelectedJobTitle('');
      setAnchorEl(null);
    }, []);

    const handleUserAssignment = React.useCallback(
      (userId: string, type: 'add' | 'remove') => {
        setAnchorEl(null);
        // Here you would typically make an API call to assign the user to the job
        if (type === 'remove') {
          setAssignedUsers((prev) =>
            prev.filter((user) => user.userId !== userId),
          );
          handleCloseAssignModal();
          return;
        }
        const userInfo = users.find((user) => user.id === userId);
        if (userInfo) {
          const newAssignedUser: JobUserAssigned = {
            assignedByFirstName: '',
            assignedByImageId: '',
            assignedByLastName: '',
            assignedByUserName: '',
            assignedToFirstName: userInfo?.firstName,
            assignedToImageId: userInfo?.imageId || null,
            assignedToLastName: userInfo?.lastName,
            assignedToUserName: userInfo?.firstName + ' ' + userInfo?.lastName,
            createdBy: userInfo.createdBy,
            id: userId,
            jobId: selectedJobId,
            userId: userId,
          };
          setAssignedUsers((prev) => [...prev, newAssignedUser]);
          handleCloseAssignModal();
        }
      },
      [users, selectedJobId, handleCloseAssignModal],
    );

    const [tooltipAnchor, setTooltipAnchor] =
      React.useState<HTMLElement | null>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
      if (assignedUsers && assignedUsers.length > 0) {
        setTooltipAnchor(event.currentTarget);
      }
    };

    const handleMouseLeave = () => {
      setTooltipAnchor(null);
    };

    const getUserDisplayName = (user: JobUserAssigned) => {
      if (user.assignedToUserName) return user.assignedToUserName;
      if (user.assignedToFirstName || user.assignedToLastName) {
        return `${user.assignedToFirstName || ''} ${
          user.assignedToLastName || ''
        }`.trim();
      }
      return 'Unknown User';
    };

    const selfAssign = React.useCallback(
      (jobId: string, userId: string) => {
        try {
          setLoader(true);
          const payload = {
            assignedTo: [...assignedUsers.map((user) => user.userId), userId],
          };
          ReplaceData(jobId, payload, `${MASTER_ENDPOINT.JobPostingAssign}`)
            .then(() => {
              setLoader(false);
              handleUserAssignment(userId, 'add');
              toast.success('Job assigned successfully', alertOptions);
            })
            .catch((err) => {
              setLoader(false);
              const errorMessage =
                err?.error?.data?.message ||
                err?.error?.message ||
                err?.message ||
                'Failed to self assign';
              toast.error(errorMessage, alertOptions);
            });
        } catch (error) {
          setLoader(false);
        }
      },
      [assignedUsers, handleUserAssignment],
    );
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
        }}>
        {assignedUsers && assignedUsers.length > 0 ? (
          <>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}>
              {assignedUsers.map((user, index) => {
                const avatarColors = ['#EBF0FF', '#FFEBEB'];
                const bgColor = avatarColors[index % avatarColors.length];
                const initials = `${
                  user?.assignedToFirstName?.charAt(0) || ''
                }${user?.assignedToLastName?.charAt(0) || ''}`.toUpperCase();

                return (
                  <Avatar
                    key={user.id}
                    sx={{
                      alignItems: 'center',
                      backgroundColor: bgColor,
                      border: '1px solid #ffffff',
                      borderRadius: '6px',
                      color: '#616161',
                      display: 'flex',
                      flexShrink: 0,
                      fontFamily: 'Inter',
                      fontSize: '10px',
                      fontWeight: 500,
                      height: '24px',
                      justifyContent: 'center',
                      marginLeft: index > 0 ? '-4px' : '0',
                      width: '24px',
                      zIndex: assignedUsers.length - index,
                    }}>
                    {initials || '-'}
                  </Avatar>
                );
              })}
            </Box>

            <Box
              component="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAssignUser(row, e.currentTarget);
              }}
              sx={{
                alignItems: 'center',
                backgroundColor: '#ffffff',
                border: '1px solid #c7c7c7',
                borderLeft: 'none',
                borderRadius: '0 6px 6px 0',
                cursor: 'pointer',
                display: 'flex',
                flexShrink: 0,
                height: '24px',
                justifyContent: 'center',
                marginLeft: '-1px',
                padding: '4px',
                width: '24px',
              }}>
              <AddCircleOutlineIcon sx={{ fontSize: '12px' }} />
            </Box>

            {/* Tooltip showing all user names on hover */}
            {tooltipAnchor && (
              <Popper
                open={Boolean(tooltipAnchor)}
                anchorEl={tooltipAnchor}
                placement="top"
                sx={{
                  zIndex: 1300,
                }}>
                <Box
                  sx={{
                    backgroundColor: '#242424',
                    borderRadius: '4px',
                    padding: '8px 12px',
                  }}>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '18px',
                      whiteSpace: 'pre-line',
                    }}>
                    {assignedUsers.map(getUserDisplayName).join('\n')}
                  </Typography>
                </Box>
              </Popper>
            )}
          </>
        ) : loader ? (
          <CircularProgress size={20} />
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#ffffff',
              border: '1px solid #c7c7c7',
              borderRadius: '6px',
              display: 'flex',
            }}>
            <Box
              component="button"
              sx={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                gap: '6px',
                justifyContent: 'center',
                padding: '4px',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selfAssign(row?.id, userDetail?.id);
              }}>
              <PersonAddIcon
                sx={{
                  color: '#616161',
                  fontSize: '16px',
                }}
              />
            </Box>
            <Box
              sx={{
                borderLeft: '1px solid #c7c7c7',
                display: 'flex',
                padding: '4px',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAssignUser(row, e.currentTarget);
              }}>
              <ExpandMoreIcon
                sx={{
                  color: '#616161',
                  fontSize: '12px',
                }}
              />
            </Box>
          </Box>
        )}
        {/* User Assign Modal */}
        <UserAssignModal
          open={assignModalOpen}
          onClose={handleCloseAssignModal}
          onAssignUser={handleUserAssignment}
          jobId={selectedJobId}
          jobTitle={selectedJobTitle}
          anchorEl={anchorEl}
          users={users}
          exisitngAssignedUsers={assignedUsers}
        />
      </div>
    );
  },
);

export default AssignUserList;
